import { useEffect } from 'react'
import { useTranslation } from '@flight-tools/i18n'
import { Card, CardHeader, CardTitle, CardContent, Progress, Alert, AlertTitle, AlertDescription, Badge } from '@flight-tools/ui'
import { ChartBar, Warning } from '@phosphor-icons/react'
import { motion, AnimatePresence } from 'framer-motion'
import type { SeatOccupancy, CabinConfiguration } from '@flight-tools/domain'

import en from './locales/en.json'
import es from './locales/es.json'
import sv from './locales/sv.json'

interface CapacityAnalysisProps {
  occupancy: SeatOccupancy
  config: CabinConfiguration
}

interface MetricProps {
  label: string
  value: number | string
  variant?: 'default' | 'accent' | 'destructive'
}

function Metric({ label, value, variant = 'default' }: MetricProps) {
  const colorClass = {
    default: 'text-foreground',
    accent: 'text-accent',
    destructive: 'text-destructive',
  }[variant]

  return (
    <div className="text-center">
      <motion.div
        key={value.toString()}
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.2 }}
        className={`font-mono text-3xl font-bold ${colorClass}`}
      >
        {value}
      </motion.div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  )
}

export function CapacityAnalysis({ occupancy, config }: CapacityAnalysisProps) {
  const { t, i18n } = useTranslation('capacity-analysis')

  // Load colocated translations
  useEffect(() => {
    i18n.addResourceBundle('en', 'capacity-analysis', en, true, true)
    i18n.addResourceBundle('es', 'capacity-analysis', es, true, true)
    i18n.addResourceBundle('sv', 'capacity-analysis', sv, true, true)
  }, [i18n])

  const isOverbooked = occupancy.overbookedCount > 0
  const isHighUtilization = occupancy.utilizationPercent > 90 && !isOverbooked

  const progressColor = isOverbooked
    ? 'from-destructive to-destructive'
    : isHighUtilization
      ? 'from-primary to-primary'
      : 'from-primary via-primary to-accent'

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ChartBar weight="duotone" className="h-6 w-6 text-primary" />
          {t('capacityAnalysis')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Row Configuration */}
        <div className="grid grid-cols-2 gap-4">
          <Metric label={t('businessRows')} value={occupancy.businessRows} variant="accent" />
          <Metric label={t('economyRows')} value={occupancy.economyRows} />
        </div>

        {/* Seat Counts */}
        <div className="grid grid-cols-3 gap-4">
          <Metric label={t('totalSeats')} value={occupancy.totalAvailableSeats} />
          <Metric label={t('occupiedSeats')} value={occupancy.occupiedSeats} />
          <Metric
            label={t('availableSeats')}
            value={occupancy.availableSeats}
            variant={occupancy.availableSeats < 0 ? 'destructive' : 'default'}
          />
        </div>

        {/* Utilization Bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{t('utilization')}</span>
            <motion.span
              key={occupancy.utilizationPercent.toFixed(1)}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className={`font-mono text-lg font-bold ${
                isOverbooked ? 'text-destructive' : isHighUtilization ? 'text-primary' : 'text-accent'
              }`}
            >
              {occupancy.utilizationPercent.toFixed(1)}%
            </motion.span>
          </div>
          <Progress
            value={Math.min(occupancy.utilizationPercent, 100)}
            className="h-3"
            indicatorClassName={`bg-gradient-to-r ${progressColor}`}
          />
        </div>

        {/* Warnings */}
        <AnimatePresence>
          {isOverbooked && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Alert variant="destructive">
                <AlertTitle className="flex items-center gap-2">
                  <Badge variant="destructive">{t('overbooked')}</Badge>
                </AlertTitle>
                <AlertDescription>
                  {t('overbookedBy', { count: occupancy.overbookedCount })}
                </AlertDescription>
              </Alert>
            </motion.div>
          )}

          {occupancy.lapInfantWarning && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Alert variant="warning">
                <AlertTitle>
                  <Warning weight="fill" className="inline h-4 w-4 mr-1" />
                  {t('lapInfants')}
                </AlertTitle>
                <AlertDescription>
                  {t('lapInfantWarning', {
                    infants: config.lapInfants,
                    passengers: config.economyPassengers,
                  })}
                </AlertDescription>
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  )
}
